import React, { useState } from 'react';
import { Table, Button, Input, Message, Icon, Confirm, Header } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { PartitionRule } from '../../app/models/CompanySportVenueAsset';

interface Props {
  assetId: string;
  totalCapacity: number;
  partitionRules: PartitionRule[];
  onAdd: (rule: Omit<PartitionRule, 'id'>) => void;
  onDelete: (ruleId: string) => void;
  isNewAsset: boolean;
}

const PartitionRulesSection: React.FC<Props> = ({
  assetId,
  totalCapacity,
  partitionRules,
  onAdd,
  onDelete,
  isNewAsset
}) => {
  const { t } = useTranslation();
  const [capacityUnits, setCapacityUnits] = useState<number>(0);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleAdd = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (capacityUnits <= 0 || capacityUnits > totalCapacity) {
      return;
    }

    // Check for duplicates
    if (partitionRules.some(r => r.capacityUnits === capacityUnits)) {
      return;
    }

    onAdd({
      companySportVenueAssetId: assetId,
      capacityUnits,
      isActive: true
    });

    setCapacityUnits(0);
  };

  const handleDelete = (ruleId: string) => {
    onDelete(ruleId);
    setDeleteConfirm(null);
  };

  // Suggest common divisions
  const suggestedDivisions = React.useMemo(() => {
    const suggestions: number[] = [];
    if (totalCapacity >= 2) {
      // Full capacity
      suggestions.push(totalCapacity);

      // Half
      if (totalCapacity % 2 === 0) {
        suggestions.push(totalCapacity / 2);
      }

      // Third
      if (totalCapacity % 3 === 0) {
        suggestions.push(totalCapacity / 3);
      }

      // Quarter
      if (totalCapacity % 4 === 0) {
        suggestions.push(totalCapacity / 4);
      }
    }

    // Filter out existing
    return suggestions.filter(s => !partitionRules.some(r => r.capacityUnits === s));
  }, [totalCapacity, partitionRules]);

  return (
    <div style={{ marginTop: '20px' }}>
      <Header as="h5">
        <Icon name="grid layout" />
        {t('assetForm.partitionRules')}
      </Header>

      <Message info size="small">
        <p>{t('assetForm.partitionRulesHelp')}</p>
      </Message>

      {/* Add new partition */}
      <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '150px' }}>
          <label style={{ fontSize: '13px', fontWeight: 'bold' }}>
            {t('assetForm.capacityUnits')}
          </label>
          <Input
            type="number"
            min="1"
            max={totalCapacity}
            value={capacityUnits || ''}
            onChange={(e) => setCapacityUnits(Number(e.target.value))}
            placeholder={t('assetForm.enterCapacity')}
            fluid
          />
        </div>
        <Button
          color="green"
          type="button"
          icon="add"
          content={t('common.add')}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleAdd();
          }}
          disabled={capacityUnits <= 0 || capacityUnits > totalCapacity}
        />
      </div>

      {/* Suggested divisions */}
      {suggestedDivisions.length > 0 && (
        <div style={{ marginBottom: '15px' }}>
          <label style={{ fontSize: '12px', color: '#666', marginBottom: '5px', display: 'block' }}>
            {t('assetForm.suggestedPartitions')}:
          </label>
          <Button.Group size="tiny">
            {suggestedDivisions.map(units => (
              <Button
                type="button"
                key={units}
                basic
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onAdd({
                    companySportVenueAssetId: assetId,
                    capacityUnits: units,
                    isActive: true
                  });
                }}
              >
                {units} {t('assetForm.units')}
              </Button>
            ))}

          </Button.Group>
        </div>
      )}

      {/* Existing rules */}
      {partitionRules.length > 0 ? (
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t('assetForm.capacityUnits')}</Table.HeaderCell>
              <Table.HeaderCell>{t('assetForm.percentage')}</Table.HeaderCell>
              <Table.HeaderCell width={2}>{t('common.actions')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {partitionRules
              .sort((a, b) => b.capacityUnits - a.capacityUnits)
              .map((rule) => (
                <Table.Row key={rule.id || rule.capacityUnits}>
                  <Table.Cell>
                    <strong>{rule.capacityUnits}</strong> {t('assetForm.units')}
                  </Table.Cell>
                  <Table.Cell>
                    {Math.round((rule.capacityUnits / totalCapacity) * 100)}% {t('assetForm.ofTotal')}
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      type="button"
                      size="tiny"
                      color="red"
                      icon="trash"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDeleteConfirm(rule.id || String(rule.capacityUnits));
                      }}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
          </Table.Body>
        </Table>
      ) : (
        <Message warning>
          <p>{t('assetForm.noPartitionRules')}</p>
        </Message>
      )}

      <Confirm
        open={deleteConfirm !== null}
        header={t('assetForm.deletePartitionRule')}
        content={t('assetForm.deletePartitionRuleConfirm')}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
      />
    </div>
  );
};

export default PartitionRulesSection;